import { useState } from 'react';
import type { TurnDebugInfo } from '../../services/api/sessions';

import { routingSourceLabel } from './customer-view';

const LANE_COLORS: Record<string, string> = {
  order_ops: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  purchase_history: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  general_info: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
  product_questions: 'bg-green-500/20 text-green-300 border-green-500/30',
  default_ai: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
};

const LANE_DEFAULT = 'bg-slate-600/30 text-slate-400 border-slate-600/30';

function confidenceColor(c?: number): string {
  if (c === undefined) return 'text-slate-400';
  if (c >= 0.85) return 'text-green-400';
  if (c >= 0.65) return 'text-yellow-400';
  return 'text-red-400';
}

interface Props {
  debug: TurnDebugInfo;
}

export function TurnDebugPanel({ debug }: Props) {
  const [expanded, setExpanded] = useState(false);
  const { routing, tools, latency, suggestedActions, stateSnapshot } = debug;

  const laneClass = routing?.lane ? (LANE_COLORS[routing.lane] ?? LANE_DEFAULT) : LANE_DEFAULT;
  const sourceLabel = routingSourceLabel(routing?.routingSource);
  const confPct =
    routing?.confidence !== undefined ? Math.round(routing.confidence * 100) + '%' : null;

  const hasStateSnapshot = !!(
    stateSnapshot &&
    (stateSnapshot.conversationMode ||
      stateSnapshot.outfitPhase ||
      stateSnapshot.outfitOccasion ||
      stateSnapshot.generalInfoState ||
      stateSnapshot.sessionMode ||
      stateSnapshot.isVerified ||
      stateSnapshot.hasOrderNumber ||
      stateSnapshot.currentSearchIntent ||
      typeof stateSnapshot.historyLength === 'number')
  );
  const hasDetails =
    (routing?.reasons?.length ?? 0) > 0 ||
    (tools?.length ?? 0) > 0 ||
    (suggestedActions?.length ?? 0) > 0 ||
    (routing?.compoundBranches?.length ?? 0) > 0 ||
    hasStateSnapshot;

  return (
    <div className="mt-2 rounded-lg border border-slate-700 bg-slate-800/50 text-xs font-mono">
      <button
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-slate-700/40 transition-colors rounded-lg"
        disabled={!hasDetails}
      >
        {routing?.lane && (
          <span
            className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${laneClass}`}
          >
            {routing.lane.replace('_', ' ')}
          </span>
        )}

        {routing?.canonicalAction && (
          <span className="rounded bg-slate-700/60 px-1.5 py-0.5 text-slate-300">
            {routing.canonicalAction}
          </span>
        )}

        {routing?.intentType && routing.intentType !== routing.canonicalAction && (
          <span className="text-slate-400">{routing.intentType}</span>
        )}

        {confPct && (
          <span
            className={`ml-auto font-semibold tabular-nums ${confidenceColor(routing?.confidence)}`}
          >
            {confPct}
          </span>
        )}

        {sourceLabel && (
          <span className="rounded bg-slate-700/50 px-1 py-0.5 text-[10px] text-slate-400">
            {sourceLabel}
          </span>
        )}

        {routing?.conflict && (
          <span className="rounded bg-yellow-900/50 px-1 py-0.5 text-[10px] text-yellow-400">
            conflict:{routing.conflict}
          </span>
        )}

        {routing?.retried && (
          <span className="rounded bg-orange-900/40 px-1 py-0.5 text-[10px] text-orange-400">
            retried
          </span>
        )}

        {latency && (
          <span className="text-slate-500">
            {latency.totalMs}ms
            {latency.toolLatencySum ? ` · ${latency.toolLatencySum}ms tools` : ''}
          </span>
        )}

        {hasDetails && (
          <svg
            className={`h-3 w-3 text-slate-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>

      {expanded && hasDetails && (
        <div className="border-t border-slate-700 px-3 py-2 space-y-3">
          {hasStateSnapshot && stateSnapshot && (
            <div>
              <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">
                State (vid turn-start)
              </div>
              <div className="flex flex-wrap gap-1">
                {stateSnapshot.conversationMode && (
                  <span className="rounded bg-indigo-500/20 border border-indigo-500/30 px-1.5 py-0.5 text-indigo-300">
                    mode:{stateSnapshot.conversationMode}
                  </span>
                )}
                {stateSnapshot.sessionMode &&
                  stateSnapshot.sessionMode !== stateSnapshot.conversationMode && (
                    <span className="rounded bg-indigo-500/15 border border-indigo-500/25 px-1.5 py-0.5 text-indigo-300">
                      session:{stateSnapshot.sessionMode}
                    </span>
                  )}
                {stateSnapshot.outfitPhase && (
                  <span className="rounded bg-orange-500/20 border border-orange-500/30 px-1.5 py-0.5 text-orange-300">
                    outfit:{stateSnapshot.outfitPhase}
                  </span>
                )}
                {stateSnapshot.outfitOccasion && (
                  <span className="rounded bg-orange-500/15 border border-orange-500/25 px-1.5 py-0.5 text-orange-300">
                    occasion:{stateSnapshot.outfitOccasion}
                  </span>
                )}
                {stateSnapshot.generalInfoState && (
                  <span className="rounded bg-slate-600/40 border border-slate-600/40 px-1.5 py-0.5 text-slate-300">
                    general_info:{stateSnapshot.generalInfoState}
                  </span>
                )}
                {stateSnapshot.isVerified && (
                  <span className="rounded bg-green-500/20 border border-green-500/30 px-1.5 py-0.5 text-green-300">
                    verified
                  </span>
                )}
                {stateSnapshot.hasOrderNumber && (
                  <span className="rounded bg-blue-500/20 border border-blue-500/30 px-1.5 py-0.5 text-blue-300">
                    order#
                  </span>
                )}
                {(stateSnapshot.currentSearchIntent?.searchQuery ||
                  stateSnapshot.currentSearchIntent?.categoryId) && (
                  <span className="rounded bg-purple-500/20 border border-purple-500/30 px-1.5 py-0.5 text-purple-300">
                    search:
                    {stateSnapshot.currentSearchIntent.searchQuery ||
                      stateSnapshot.currentSearchIntent.categoryId}
                  </span>
                )}
                {typeof stateSnapshot.historyLength === 'number' && (
                  <span className="rounded bg-slate-700/60 px-1.5 py-0.5 text-slate-400">
                    history:{stateSnapshot.historyLength}
                  </span>
                )}
              </div>
            </div>
          )}

          {routing?.compoundBranches && routing.compoundBranches.length > 0 && (
            <div>
              <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">
                Compound branches
              </div>
              <div className="space-y-1">
                {routing.compoundBranches.map((b, i) => (
                  <div key={i} className="flex items-start gap-2 text-slate-300">
                    <span className="shrink-0 text-slate-500">{i + 1}.</span>
                    <span className="truncate">{b.clause}</span>
                    <span
                      className={`ml-auto shrink-0 rounded border px-1 py-0 text-[10px] ${LANE_COLORS[b.lane] ?? LANE_DEFAULT}`}
                    >
                      {b.lane}
                    </span>
                    {b.canonicalAction && (
                      <span className="shrink-0 text-slate-400">{b.canonicalAction}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {routing?.reasons && routing.reasons.length > 0 && (
            <div>
              <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">
                Reasons
              </div>
              <div className="flex flex-wrap gap-1">
                {routing.reasons.map((r) => (
                  <span key={r} className="rounded bg-slate-700/50 px-1.5 py-0.5 text-slate-300">
                    {r}
                  </span>
                ))}
              </div>
            </div>
          )}

          {tools && tools.length > 0 && (
            <div>
              <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Tools</div>
              <table className="w-full border-collapse text-[11px]">
                <thead>
                  <tr className="text-slate-500">
                    <th className="pb-1 text-left font-normal">Tool</th>
                    <th className="pb-1 text-right font-normal">ms</th>
                    <th className="pb-1 text-left pl-3 font-normal">Status</th>
                    <th className="pb-1 text-left pl-3 font-normal">Output</th>
                  </tr>
                </thead>
                <tbody>
                  {tools.map((t, i) => (
                    <tr key={i} className="text-slate-300">
                      <td className="py-0.5 pr-2">{t.name}</td>
                      <td className="py-0.5 text-right tabular-nums text-slate-400">
                        {t.durationMs}
                      </td>
                      <td className="py-0.5 pl-3">
                        <span
                          className={t.status === 'success' ? 'text-green-400' : 'text-red-400'}
                        >
                          {t.status}
                        </span>
                      </td>
                      <td className="py-0.5 pl-3 text-slate-400">{t.outputSummary ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {suggestedActions && suggestedActions.length > 0 && (
            <div>
              <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">
                Suggested actions
              </div>
              <div className="flex flex-wrap gap-1">
                {suggestedActions.map((a, i) => (
                  <span
                    key={i}
                    className="rounded border border-slate-600 bg-slate-700/40 px-2 py-0.5 text-slate-300"
                    title={a.intentType ?? a.value}
                  >
                    {a.label}
                    {a.intentType && <span className="ml-1 text-slate-500">·{a.intentType}</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
